import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';

import EmbeddedMMDrawer from 'components/EmbeddedMMDrawer';
import { JobDocsDocumentHeader } from 'components/JobDocsDocumentHeader';
import { useJobDocsConfig } from 'hooks/useJobDocsConfig';
import { normalizeClientUrl } from 'lib/jobDocs/normalizeClientUrl';
import {
  JOB_DOCS_EVENT_SET_SUMMARY,
  JOB_DOCS_CONTEXT_DOC_BACK_KEY,
} from 'lib/jobDocs/embedContract';

// Restrict the embedded job-docs-client to what it actually needs:
//   - allow-scripts: it's a React SPA
//   - allow-same-origin: same-origin XHR/fetch for documents + postMessage
//   - allow-forms: document linking / search forms
//   - allow-popups: opening a document in a new tab
export const JOB_DOCS_IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups';

export const JOB_DOCS_REFERRER_POLICY = 'strict-origin-when-cross-origin';

// No open document: the embed is showing its grid.
const NO_SUMMARY = { title: null, fileLabel: null };

// How long the loader waits for the embed's 'ready' before giving the
// operator a way out (Close/Retry) instead of a spinner forever.
export const JOB_DOCS_LOAD_TIMEOUT_MS = 15000;

const TimeoutFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  text-align: center;
  font-size: 0.875rem;
`;

const TimeoutButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
`;

// 40px-minimum touch targets, matching the header/section buttons.
const TimeoutButton = styled.button`
  min-width: 5rem;
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--grey-300, #e2e8f0);
  border-radius: 6px;
  background: white;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &:focus-visible {
    outline: 2px solid var(--blue-500, #3182ce);
    outline-offset: 2px;
  }
`;

/**
 * The Job Docs drawer: the single approved surface for the embedded
 * job-docs-client. Product rule — embeds open via approved surfaces (a
 * trigger button → EmbeddedMMDrawer), never inline inside a collapsible.
 *
 * The embedded app owns the documents experience — grid and in-place document
 * view — so the host mounts exactly ONE zone inside the drawer and feeds it:
 *
 * - `src`: the client origin (the zone uses it only for the iframe origin),
 * - `path`: the stable grid route from {@link useJobDocsIframe},
 * - `context`: `{ docBackRequestedAt }` — an epoch-ms nonce the host bumps to
 *   ask the embed to close its open document and return to the grid. It is
 *   the only context field; live metrics (activity start, parts made) render
 *   host-side in {@link JobDocsDocumentHeader}, never crossing the boundary.
 *
 * While a document is open the embed announces it via a `set-summary` zone
 * message (`{ summary: { title, fileLabel } }`, both null back on the grid);
 * the drawer mirrors that into a contextual header banner above the zone.
 * The header's X only bumps `docBackRequestedAt` — the embed closes the doc
 * and sends the null summary, which is what hides the header (single source
 * of truth; never hidden optimistically).
 */
const JobDocsDrawer = ({
  embedZoneId,
  isOpen,
  onDismiss,
  gridPath,
  partNumber,
  operationId,
}) => {
  const { t } = useTranslation();
  const { clientUrl } = useJobDocsConfig();
  // Shared normalization with useJobDocsIframe (via normalizeClientUrl) so the
  // render gate (which checks the normalized value) and the zone src can never
  // diverge.
  const normalizedClientUrl = normalizeClientUrl(clientUrl);

  const [summary, setSummary] = useState(NO_SUMMARY);
  const [docBackRequestedAt, setDocBackRequestedAt] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setSummary(NO_SUMMARY);
      setDocBackRequestedAt(0);
    }
  }, [isOpen]);

  useEmbeddedEffect(
    embedZoneId,
    JOB_DOCS_EVENT_SET_SUMMARY,
    useCallback((payload) => {
      const next = payload?.summary;
      // Cross-frame input: accept only strings so a malformed or malicious
      // payload can never put a non-string into the header (React would
      // throw on objects; numbers/booleans would render misleadingly).
      setSummary({
        title: next && typeof next.title === 'string' ? next.title : null,
        fileLabel:
          next && typeof next.fileLabel === 'string' ? next.fileLabel : null,
      });
    }, [])
  );

  const handleDocumentClose = useCallback(() => {
    setDocBackRequestedAt(Date.now());
  }, []);

  // Never-ready escape hatch: after JOB_DOCS_LOAD_TIMEOUT_MS without the
  // embed's 'ready', the loader swaps its spinner for this — a brief message
  // plus Close (dismiss the drawer) and Retry (remount the zone via the
  // `retry` EmbeddedMMDrawer hands us, restarting the load and the timer).
  const renderLoaderTimeoutFallback = useCallback(
    ({ retry }) => {
      return (
        <TimeoutFallback role="alert" data-testid="job-docs-load-timeout">
          <span>{t('Documents failed to load')}</span>
          <TimeoutButtonRow>
            <TimeoutButton type="button" onClick={onDismiss}>
              {t('Close')}
            </TimeoutButton>
            <TimeoutButton type="button" onClick={retry}>
              {t('Retry')}
            </TimeoutButton>
          </TimeoutButtonRow>
        </TimeoutFallback>
      );
    },
    [t, onDismiss]
  );

  // Memoized on the nonce so a stable value never refires the zone's
  // `set-context`. Always an object — EmbeddedMMDrawer only renders the zone
  // when `context` is non-null.
  const context = useMemo(() => {
    return { [JOB_DOCS_CONTEXT_DOC_BACK_KEY]: docBackRequestedAt };
  }, [docBackRequestedAt]);

  // Callers gate opening on gridUrl, but the drawer stays mounted for state —
  // without a route or origin there is nothing to host.
  if (!gridPath || !normalizedClientUrl) {
    return null;
  }

  return (
    <EmbeddedMMDrawer
      embedZoneId={embedZoneId}
      messageSource="job-docs"
      src={normalizedClientUrl}
      path={gridPath}
      sandbox={JOB_DOCS_IFRAME_SANDBOX}
      referrerPolicy={JOB_DOCS_REFERRER_POLICY}
      context={context}
      isOpen={isOpen}
      onDismiss={onDismiss}
      defaultTitle={t('Documents')}
      // The drawer shows no host chrome (hideHeader below), so the dialog
      // needs an explicit accessible name.
      ariaLabel={t('Documents')}
      loaderTimeoutMs={JOB_DOCS_LOAD_TIMEOUT_MS}
      renderLoaderTimeoutFallback={renderLoaderTimeoutFallback}
      // Figma: the docs drawer shows NO host chrome in either state. The grid
      // toolbar carries its own back arrow (embed-side, sends `job-docs:close`
      // → onDismiss below), and the doc view's contextual header is the top
      // bar. Overlay tap / ESC still dismiss via the Chakra Drawer.
      hideHeader
      banner={
        summary.title != null ? (
          <JobDocsDocumentHeader
            title={summary.title}
            fileLabel={summary.fileLabel}
            partNumber={partNumber}
            operationId={operationId}
            onClose={handleDocumentClose}
          />
        ) : null
      }
    />
  );
};

JobDocsDrawer.propTypes = {
  /** Embed zone id — stable per mount so the interop handshake stays bound. */
  embedZoneId: PropTypes.string.isRequired,
  /** Whether the drawer is open (parent owns the state). */
  isOpen: PropTypes.bool.isRequired,
  /** Closes the drawer. */
  onDismiss: PropTypes.func.isRequired,
  /** Grid route (pathname+search) from useJobDocsIframe. */
  gridPath: PropTypes.string,
  /**
   * Part number of the operation the drawer was opened for — passed straight
   * to the contextual header's operation label (numeric `0` is a valid id).
   */
  partNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Operation id / sequence number of that operation. */
  operationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

JobDocsDrawer.defaultProps = {
  gridPath: null,
  partNumber: null,
  operationId: null,
};

export { JobDocsDrawer };
